import cloneDeep from 'lodash/cloneDeep';
import { TextField, Typography, OutlinedInput, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isNil } from 'lodash';
import { log } from '@/logger/logger.ts';
// import { Video } from '@/UI/Video/Video';
import { getAxiosInstance } from '@/services/axiosConfig';
import { clampValue } from '@/utils/numbers';
import { ModelType } from '@/enums/model-type.enum';
import { color } from '@/colors';
import ThreeDeeViewer from './ThreeDeeViewer';
import type { MediaAsset } from '@/types/api/assets';
import type { Model, ModelBaseNodeData } from '@/types/nodes/model';
import type { WorkflowRole, ModelPricesMaps } from '@/state/workflow.state';
import type { ModelPrice } from '@/types/api/modelPrice';
import type { Edge, MergeAlphaData, Node, Schema } from '@/types/node';

// import AnyLLMIcon from '@/UI/Icons/sidebar/

const logger = log.getLogger('Utils');
const axiosInstance = getAxiosInstance();

export const ExtraSmallFontTextField = styled(TextField)`
  & .MuiInputBase-root,
  & {
    font-size: 0.6rem;
  }

  & .MuiSelect-select {
    padding: 4px 6px;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-radius: 4px;
  }
`;

export const SmallFontTextField = styled(TextField)`
  & .MuiInputBase-root,
  & {
    font-size: 0.8rem;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-radius: 4px;
  }
`;

export const getImageDimensions = async (image: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      resolve({ width, height });
    };

    img.onerror = (error) => {
      logger.error('Failed to load image', error);
      reject(new Error('Failed to load image'));
    };

    img.src = image;
  });
};

export const getFileDimensions = (file: string, type: 'image' | 'video') => {
  return new Promise((resolve, reject) => {
    // For images
    if (type === 'image') {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = file;
    }
    // For videos
    else if (type === 'video') {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = reject;
      video.src = file;
    } else {
      reject(new Error('Unsupported file type'));
    }
  });
};

const isMergeAlphaData = (data: unknown): data is MergeAlphaData => {
  return !!data && typeof data === 'object' && 'inputNode' in data;
};

const isBase64DataUrl = (value: string) => {
  return value.includes('data:image/png;base64') || value.includes('data:image/jpeg;base64');
};

const cleanBase64DataUrlsInPlace = (root: unknown) => {
  const sanitize = (value: unknown) => {
    if (!value || typeof value === 'string') {
      return;
    }

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        if (typeof item === 'string' && isBase64DataUrl(item)) {
          value[i] = '';
          continue;
        }
        sanitize(item);
      }
      return;
    }

    if (typeof value !== 'object') {
      return;
    }

    const record = value as Record<string, unknown>;
    for (const [key, child] of Object.entries(record)) {
      if (typeof child === 'string') {
        if (isBase64DataUrl(child)) {
          record[key] = '';
        }
        continue;
      }
      sanitize(child);
    }
  };

  sanitize(root);
};

export function sanitizeNodes(nodesToSanitize: Node[]) {
  const clonedNodes = cloneDeep(nodesToSanitize);
  // Now you can safely modify sanitizedNode since it's deeply cloned
  return clonedNodes.map((node) => {
    if (
      node.type === 'compv3' &&
      'data' in node.data &&
      node.data.data &&
      typeof node.data.data === 'object' &&
      'input' in node.data.data &&
      Array.isArray(node.data.data.input)
    ) {
      for (const [i, input] of node.data.data.input.entries()) {
        // dynamic compositor input will be filled in at runtime
        if (input[1]?.file?.url.includes('data:image/png;base64')) {
          node.data.data.input[i][1].file = undefined;
        }
      }
    }
    if (isMergeAlphaData(node.data) && node.data.inputNode?.file?.url.includes('data:image/png;base64')) {
      node.data.inputNode.file = undefined;
    }
    if (isMergeAlphaData(node.data) && node.data.alphaInput?.file?.url.includes('data:image/png;base64')) {
      node.data.alphaInput.file = undefined;
    }

    // Keep the targeted handling above intact; this catches any remaining base64 data-URLs anywhere else.
    cleanBase64DataUrlsInPlace(node);

    return node;
  });
}

export const sanitizeEdges = (edgesToSanitize: Edge[], nodes: Node[]) => {
  const nodeMap = new Set(nodes.map((node) => node.id));
  return edgesToSanitize.filter((edge) => {
    return nodeMap.has(edge.source) && nodeMap.has(edge.target);
  });
};

export const sanitizeRecipe = (nodes: Node[], edges: Edge[]) => {
  const sanitizedNodes = sanitizeNodes(nodes);
  const sanitizedEdges = sanitizeEdges(edges, sanitizedNodes);

  return { nodes: sanitizedNodes, edges: sanitizedEdges };
};

/// No longer used?
// function performMatteChoke(imageData, amount) {
//   const width = imageData.width;
//   const height = imageData.height;
//   const data = new Uint8ClampedArray(imageData.data);
//   const result = new Uint8ClampedArray(data.length);

//   const threshold = 128;
//   const iterations = Math.abs(amount);
//   const expand = amount > 0;

//   for (let i = 0; i < iterations; i++) {
//     for (let y = 0; y < height; y++) {
//       for (let x = 0; x < width; x++) {
//         const idx = (y * width + x) * 4;
//         let sum = 0;
//         let count = 0;

//         // Check neighboring pixels
//         for (let dy = -1; dy <= 1; dy++) {
//           for (let dx = -1; dx <= 1; dx++) {
//             const nx = x + dx;
//             const ny = y + dy;
//             if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
//               const nidx = (ny * width + nx) * 4;
//               sum += data[nidx] > threshold ? 1 : 0;
//               count++;
//             }
//           }
//         }

//         // Determine if the pixel should be white or black
//         const shouldBeWhite = expand ? sum > 0 : sum === count;
//         const value = shouldBeWhite ? 255 : 0;

//         // Set the result pixel
//         result[idx] = result[idx + 1] = result[idx + 2] = value;
//         result[idx + 3] = 255; // Alpha channel
//       }
//     }

//     // Copy result back to data for next iteration
//     data.set(result);
//   }

//   return new ImageData(result, width, height);
// }

// export function matteChoker2(base64Image, amount) {
//   return new Promise((resolve, reject) => {
//     const img = new Image();
//     img.onload = () => {
//       const canvas = document.createElement('canvas');
//       const ctx = canvas.getContext('2d');

//       if (!ctx) {
//         reject(new Error('Failed to get canvas context'));
//         return;
//       }

//       canvas.width = img.width;
//       canvas.height = img.height;
//       ctx.drawImage(img, 0, 0);

//       const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
//       const chokedImageData = performMatteChoke(imageData, amount);

//       ctx.putImageData(chokedImageData, 0, 0);
//       resolve(canvas.toDataURL());
//     };
//     img.onerror = reject;
//     img.src = base64Image;
//   });
// }

// bria register image
export const registerImageUtil = async (url: string) => {
  const res = await axiosInstance.post<{ visualId: string }>('/v1/models/image/register', { url });

  return res.data.visualId;
};
//
// No longer used?
// export const flattenMenuItems = (items, searchTerm) => {
//   let flatItems = [];
//   const uniqueIds = new Set();

//   const searchAndFlatten = (item) => {
//     Object.keys(item).forEach((key) => {
//       const currentItem = item[key];

//       // Check if we have a valid item without children
//       if (!currentItem.children && currentItem.id && !uniqueIds.has(currentItem.id)) {
//         const searchTermLower = searchTerm.toLowerCase();

//         // Check displayName
//         const matchesDisplayName = currentItem.displayName.toLowerCase().includes(searchTermLower);

//         // Check aliases
//         const matchesAlias = currentItem.alias?.some((alias) => alias.toLowerCase().includes(searchTermLower));

//         if (matchesDisplayName || matchesAlias) {
//           flatItems.push(currentItem);
//           uniqueIds.add(currentItem.id);
//         }
//       }

//       // Continue searching in children
//       if (currentItem.children) {
//         searchAndFlatten(currentItem.children);
//       }
//     });
//   };

//   searchAndFlatten(items);

//   return flatItems;
// };

export const cleanParamsForSaving = (params: ModelBaseNodeData['params'], latestSeed: number) => {
  const cleanParams = cloneDeep(params);
  if (cleanParams && cleanParams.seed) {
    cleanParams.seed = latestSeed;
  }

  return cleanParams;
};

/// IMAGE processing functions

export function rgbaToRgb(imageUrl: string, format = 'jpeg') {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // Enable cross-origin if needed
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Set the canvas size to match the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image on the canvas
      ctx.drawImage(img, 0, 0);

      // Convert the canvas back to a data URL in the desired format
      const rgbImageUrl = canvas.toDataURL(`image/${format}`);

      // Cleanup: remove the canvas
      canvas.remove();

      resolve(rgbImageUrl);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image.'));
    };
  });
}

/**
 * Negates the colors of an image from a URL or base64 string.
 * @param {string} imageSource - URL or base64 string of the image
 * @returns {Promise<string>} Promise that resolves to a base64 string of the negated image
 */
export async function negateImageColors(imageSource: string) {
  // Create an off-screen canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Create and load the image
  const image = new Image();
  image.crossOrigin = 'Anonymous'; // Enable CORS if needed

  // Wait for the image to load
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = imageSource;
  });

  // Set canvas size to match image
  canvas.width = image.width;
  canvas.height = image.height;

  // Draw image to canvas
  ctx.drawImage(image, 0, 0);

  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Invert colors
  for (let i = 0; i < imageData.data.length; i += 4) {
    imageData.data[i] = 255 - imageData.data[i]; // Red
    imageData.data[i + 1] = 255 - imageData.data[i + 1]; // Green
    imageData.data[i + 2] = 255 - imageData.data[i + 2]; // Blue
    // Alpha channel (i + 3) remains unchanged
  }

  // Put the negated image data back
  ctx.putImageData(imageData, 0, 0);

  // Return as base64 string
  return canvas.toDataURL('image/png');
}

export const getHandleId = (nodeId, handleType, handleName) => {
  return `${nodeId}-${handleType}-${handleName}`;
};

/// programmatic node creation utils
export const getNodeTemplates = (type: string, param: unknown, schema: Schema) => {
  switch (type) {
    case 'number':
    case 'integer':
    case 'input-number':
    case 'input-integer': {
      const mode = schema.type === 'integer' || schema.type === 'input-integer' ? 'integer' : 'number';

      return {
        handleName: 'number',
        template: {
          id: 'JbQzwNX9qVhHP59o5yS1Clone',
          type: 'number',
          initialData: {
            name: schema.title,
            description: schema.description,
            value: clampValue(Number(param), Number(schema.min), Number(schema.max)),
            min: schema.min,
            max: schema.max,
            step: schema.step,
            mode: mode,
          },
        },
      };
    }
    case 'boolean':
      return {
        handleName: 'option',
        template: {
          id: 'UpdngxSEck7aHx92LkSgClone',
          initialData: {
            name: schema.title,
            description: schema.description,
            value: param,
          },
        },
      };
    case 'enum': {
      const isNumber = (schema.options as unknown[]).every((item) => typeof item === 'number');
      const i = (schema.options as unknown[]).indexOf(param);
      const index = i >= 0 && schema.options && i < schema.options.length ? i : 0;
      return {
        handleName: 'option',
        template: isNumber
          ? {
              id: 'number_selector',
              initialData: {
                name: schema.title,
                description: schema.description,
                selectedIndex: index,
                params: {
                  options: schema.options,
                },
              },
            }
          : {
              id: 'K4Bi14QsmaOKQMHpZstoClone',
              initialData: {
                name: schema.title,
                description: schema.description,
                selectedIndex: index,
                params: {
                  options: schema.options,
                },
                list: schema.options,
              },
            },
      };
    }
    case 'text':
      return {
        handleName: 'text',
        template: {
          id: 'iB0UfxX3z6c1LCTaLH65Clone',
          initialData: {
            name: schema.title,
            description: schema.description,
            value: param,
          },
        },
      };
    case 'array':
      return {
        handleName: 'array',
        template: {
          id: 'E4Lo1vBJYlSjvgs0LtcSClone',
          initialData: {
            name: schema.title,
            description: schema.description,
            array: param ? (Array.isArray(param) ? param.map(String) : param) : [''],
            placeholder: `${String(schema.title)} item`,
          },
        },
      };
    case 'seed':
      return {
        handleName: 'seed',
        template: {
          id: 'HMTea9nBdmeBeoCAdxvzClone',
          initialData: {
            name: schema.title,
            description: schema.description,
            isRandom: (param as { isRandom: boolean }).isRandom,
            seed: (param as { seed: number }).seed,
          },
        },
      };
  }
};

/**

Determines the model type based on the provided model object.
@param {Object} model - The model object to evaluate.
@param {'replicate' | 'civit' | null=} model.service - The service type of the model.
@param {string} model.name - The name of the model.
@returns {ModelType} The determined model type.
*/
export const getModelType = (model: Partial<Model>) => {
  if (
    model.service === ModelType.Replicate.valueOf() ||
    model.service === ModelType.Civit.valueOf() ||
    model.service === ModelType.FalImported.valueOf()
  ) {
    return model.service as ModelType;
  }

  const enumValues = Object.values(ModelType).map((value) => value.valueOf());
  if (model.name && enumValues.includes(model.name)) {
    return model.name as ModelType;
  }

  if (model.name?.includes('/civitai.com')) {
    return ModelType.Civit;
  }

  if (model.name?.includes('/fal.ai') || model.name?.includes('fal-ai')) {
    return ModelType.FalImported;
  }

  return ModelType.Replicate;
};

const findPriceByVariantAndExtraData = (
  modelPrices: ModelPrice[],
  modelName: string | null,
  extraData: string | null,
) => {
  if (modelName && extraData) {
    const exactMatch = modelPrices.find((obj) => obj.modelVariant === modelName && obj.extraData === extraData);
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Priority 2: Match modelVariant only
  if (modelName) {
    const variantMatch = modelPrices.find((obj) => obj.modelVariant === modelName);
    if (variantMatch) {
      return variantMatch;
    }
  }
  // Priority 3: Match extraData only
  if (extraData) {
    const extraDataMatch = modelPrices.find((obj) => obj.extraData === extraData);
    if (extraDataMatch) {
      return extraDataMatch;
    }
  }
};

export const RELEVANT_PRICING_MODEL_PARAMS = [
  'duration',
  'quality',
  'motion_mode',
  'rendering_speed',
  'resolution',
  'model',
  'generate_audio',
  'version',
] as const;

const KLING_AUDIO_MULTIPLIER = 1.5;
const SEEDANCE2_AUDIO_MULTIPLIER = 1;
export type ModelPriceLookupOptions = {
  workspaceFalByokEnabled?: boolean;
};
export type ResolvedModelPrice = {
  credits: number;
  isByok: boolean;
};

const getAudioMultiplier = (modelName: string | null, modelType: ModelType, inputModel: string | null) => {
  const isKlingV3 =
    modelName?.includes('kling-video/v3/') ||
    modelName?.includes('kling-video/o3/') ||
    (modelType === ModelType.Kling && inputModel?.includes('3.0'));

  const isSeedance2 = modelName?.includes('seedance-2.0');
  if (isKlingV3) {
    return KLING_AUDIO_MULTIPLIER;
  }
  if (isSeedance2) {
    return SEEDANCE2_AUDIO_MULTIPLIER;
  }
  return 1;
};

const getModelPriceByVariantAndParams = (
  modelPrices: ModelPrice[],
  modelInput: ModelBaseNodeData['params'],
  inputModel: string | null,
) => {
  const duration = !isNil(modelInput?.duration) ? String(modelInput.duration) : null;
  const quality = modelInput?.quality ? String(modelInput.quality) : null;
  const resolution = modelInput?.resolution ? String(modelInput.resolution) : null;
  const motion_mode = modelInput?.motion_mode ? String(modelInput.motion_mode) : null;
  const rendering_speed = modelInput?.rendering_speed ? String(modelInput.rendering_speed) : null;
  const version = modelInput?.version ? String(modelInput.version) : null;
  const extraData = duration || quality || motion_mode || rendering_speed || resolution || version;
  const foundPrice = findPriceByVariantAndExtraData(modelPrices, inputModel, extraData);
  return foundPrice ?? modelPrices[0];
};

/** Resolved model pricing details including final credits and BYOK status. */
export const getModelPriceInfo = (
  model: Partial<Model>,
  modelPricesMaps: ModelPricesMaps,
  modelInput: ModelBaseNodeData['params'],
  options?: ModelPriceLookupOptions,
): ResolvedModelPrice | undefined => {
  if (!modelPricesMaps?.modelsByType?.size) {
    return undefined;
  }

  const modelName = model.name;
  const modelType = getModelType(model);
  const inputModel = modelInput?.model ? String(modelInput.model) : null;
  const audioMultiplier = modelInput?.generate_audio ? getAudioMultiplier(modelName ?? null, modelType, inputModel) : 1;
  const workspaceFalByokEnabled = options?.workspaceFalByokEnabled === true;

  let modelPrices: ModelPrice[] | undefined;
  if (modelName && (modelType === ModelType.Replicate || modelType === ModelType.FalImported)) {
    modelPrices = modelPricesMaps.modelsByName.get(modelName);
  } else {
    modelPrices = modelPricesMaps.modelsByType.get(modelType);
  }
  if (!modelPrices?.length) {
    if (modelType === ModelType.FalImported && workspaceFalByokEnabled) {
      return { credits: 0, isByok: true };
    }
    return { credits: modelPricesMaps.defaultPrice, isByok: false };
  }

  let entry: ModelPrice;
  if (modelPrices.length === 1) {
    entry = modelPrices[0];
  } else {
    entry = getModelPriceByVariantAndParams(modelPrices, modelInput, inputModel);
  }

  const isByok = entry.isByok === true;
  return {
    isByok,
    credits: isByok ? 0 : entry.credits * audioMultiplier,
  };
};

export const hasEditingPermissions = (role: WorkflowRole | null, data: { isLocked?: boolean }) => {
  if (role !== 'editor') {
    return false;
  }
  if (data.isLocked) {
    return false;
  }

  return true;
};

export const uploadFile = async (
  acceptedFiles: File[],
  setUploadProgress: (progress: number) => void,
  uploadSuccess: (data: MediaAsset) => void,
  setHasError: (hasError: boolean) => void,
) => {
  if (acceptedFiles.length > 0) {
    const file = acceptedFiles[0];

    const formData = new FormData();
    formData.append('file', file);
    if (file.type || file.name.toLowerCase().endsWith('.heic')) {
      formData.append('type', file.type || 'image/heic');
    }
    // formData.append("media_metadata", true);

    try {
      setUploadProgress(0); // Initialize the progress bar
      const response = await axiosInstance.post<MediaAsset>(`/v1/assets/upload`, formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 95) / (progressEvent.total || (progressEvent.loaded || 1) * 100),
          );
          setUploadProgress(progress);
        },
      });

      uploadSuccess(response.data);
    } catch (error) {
      logger.error('Error uploading file', error);
      setHasError(true);
    } finally {
      setUploadProgress(0); // Reset the progress bar
    }
  }
};

function VideoPlayer({ mediaUrl }: { mediaUrl: string }) {
  return (
    <Box sx={{ position: 'relative' }}>
      <video src={mediaUrl} crossOrigin="anonymous" draggable="false" width="100%" controls loop />
    </Box>
  );
}

export function renderMediaElement(
  mediaUrl: string,
  mediaType: string,
  containerSize: {
    w: number;
    h: number;
  },
) {
  if (!mediaUrl || !mediaType) {
    return null; // or a default placeholder
  }
  switch (true) {
    case mediaType.includes('video'):
      return <VideoPlayer mediaUrl={mediaUrl} />;
    case mediaType.includes('image'):
      return (
        <>
          <img
            draggable="false"
            src={mediaUrl}
            width="100%"
            height="100%"
            alt="Media"
            style={{ display: 'block', objectFit: 'contain' }}
          />
        </>
      );
    case mediaType.includes('audio'):
      return (
        <audio
          draggable="false"
          crossOrigin="anonymous"
          src={mediaUrl}
          controls
          style={{ display: 'block', width: '100%' }}
        />
      );
    case mediaType.includes('3D'):
      return (
        <Box className="nodrag">
          <ThreeDeeViewer objUrl={mediaUrl} containerSize={containerSize} />
        </Box>
      );
    // Add more cases for other types
    default:
      return <Typography variant="body-std-rg">Preview</Typography>;
  }
}

export const SmallOutlinedInput = styled(OutlinedInput)`
  & .MuiInputBase-root,
  & {
    font-size: 0.6rem;
    height: 26px;
  }

  & .MuiInputBase-input {
    padding: 4px 6px;

    /* Remove arrows from number input */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }

  & .MuiOutlinedInput-notchedOutline {
    border-radius: 4px;
  }

  & .MuiInputAdornment-root {
    font-size: 0.65rem;
  }

  // Change selection outline to white
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${color.Yambo_Idle_Icon} !important;
    border-width: 1px;
  }

  // Also ensure the fieldset border is white when focused
  & fieldset {
    &.Mui-focused {
      border-color: ${color.Yambo_Idle_Icon} !important;
    }
  }

  max-width: 70px;
  margin-left: 8px;
`;

export const ExtraSmallOutlinedInput = styled(OutlinedInput)`
  & .MuiInputBase-root,
  & {
    font-size: 0.6rem;
    height: 26px;
    background-color: ${color.Black100};
  }

  & .MuiInputBase-input {
    padding: 4px 6px;

    /* Remove arrows from number input */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }

  & .MuiOutlinedInput-notchedOutline {
    border-radius: 4px;
  }

  & .MuiInputAdornment-root {
    font-size: 0.65rem;
  }

  // Change selection outline to white
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${color.Yambo_Idle_Icon} !important;
    border-width: 1px;
  }

  // Also ensure the fieldset border is white when focused
  & fieldset {
    &.Mui-focused {
      border-color: ${color.Yambo_Idle_Icon} !important;
    }
  }

  max-width: 70px;
  margin-left: 8px;
`;

export const generateViewingUrl = (file: MediaAsset) => {
  if (!file?.url || !file?.publicId) {
    return '';
  }
  const extension = file.url.split('.').pop();
  const publicId = file.publicId.replace('uploads/', '');
  return `${window.location.origin}/view/${publicId}?type=${file.type}&extension=${extension}`;
};

export const generateCloudinaryUrl = (publicId, extension, type) => {
  return `https://media.weavy.ai/${type}/upload/uploads/${publicId}.${extension}`;
};
